<template>
  <footer class="app-footer">
    <div class="info-grid">
      <div class="info-grid-inner">
        <div class="info-section">
          <h2>{{ $t('footer.selectYourDraw') }}</h2>
          <p>{{ $t('footer.selectYourDrawText') }}</p>
        </div>

        <div class="info-section">
          <h2>{{ $t('footer.chooseYourNumber') }}</h2>
          <p>{{ $t('footer.chooseYourNumberText') }}</p>
        </div>

        <div class="info-section">
          <h2>{{ $t('footer.submitYourBet') }}</h2>
          <p>{{ $t('footer.submitYourBetText') }}</p>
        </div>
      </div>
    </div>

    <div class="features-content">
      <div class="features-content-inner">
        <div class="feature-item">
          <div class="icon-wrapper">
            <img
              :src="footerDraws"
              alt="Fast"
              width="60"
              height="60"
            >
          </div>
          <h3>{{ $t('footer.widestSelection') }}</h3>
        </div>

        <div class="feature-item">
          <div class="icon-wrapper">
            <img
              :src="footerGlobalLotteries"
              alt="Lucky Numbers"
              width="60"
              height="60"
            >
          </div>
          <h3>{{ $t('footer.globalLuckyNumbers') }}</h3>
        </div>

        <div class="feature-item">
          <div class="icon-wrapper">
            <img
              :src="footerGreatMarkets"
              alt="Great Markets"
              width="60"
              height="60"
            >
          </div>
          <h3>{{ $t('footer.greatMarkets') }}</h3>
        </div>

        <div class="feature-item">
          <div class="icon-wrapper">
            <img
              :src="footerFastResults"
              alt="Fast Results"
              width="60"
              height="60"
            >
          </div>
          <h3>{{ $t('footer.fastResults') }}</h3>
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="footer-bottom-container">
        <router-link
          :to="{ path: '/terms-and-conditions', query: route.query }"
          class="footer-bottom-link"
        >
          {{ $t('nav.termsAndConditions') }}
        </router-link> | <router-link
          :to="{ path: '/wagers', query: route.query }"
          class="footer-bottom-link"
        >
          {{ $t('nav.wagers') }}
        </router-link> | <span class="footer-bottom-version">{{ $t('common.version') }}: 1.0.1</span>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { defineOptions } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme } from '@/composables/useTheme'

defineOptions({
  name: 'AppFooter'
})

const route = useRoute()
const { getAssetPath } = useTheme()

const footerDraws = computed(() => getAssetPath('footer', 'draws') || '/img/images/footer/01-draws.png')
const footerGlobalLotteries = computed(() => getAssetPath('footer', 'globalLotteries') || '/img/images/footer/02-global-lotteries.png')
const footerGreatMarkets = computed(() => getAssetPath('footer', 'greatMarkets') || '/img/images/footer/03-great-markets.png')
const footerFastResults = computed(() => getAssetPath('footer', 'fastResults') || '/img/images/footer/04-fast-results.png')
</script>
<style scoped>
.app-footer {
  width: 100%;
  margin: 0;
  padding: 30px 0 0;
  background: transparent;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--app-footer-card-gap, 16px);
}

.info-grid,
.features-content {
  width: 100%;
  box-sizing: border-box;
  border: none;
  outline: none;
}

.info-grid {
  background: var(--app-footer-info-bg, var(--footer-bg, var(--colour-surface-cont-low))) !important;
  padding: 32px 16px;
  border-radius: var(--app-footer-info-radius, 0);
}

.info-grid-inner {
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-section h2 {
  color: var(--app-footer-info-heading-color, var(--colour-orange-text-colour));
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

.info-section p {
  color: var(--app-footer-info-body-color, var(--app-footer-info-text-color, var(--colour-text-inverse)));
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
}

.features-content {
  background: var(--app-footer-feature-bg, var(--footer-bg-2, var(--footer-bg, var(--colour-surface-cont-low)))) !important;
  padding: 32px 16px;
  border-radius: var(--app-footer-feature-radius, 0);
}

.features-content-inner {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 24px;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--colour-icons);
}
.icon-wrapper img {
  filter: var(--footer-icon-filter, none);
}
.feature-item h3 {
  color: var(--app-footer-feature-text-color, var(--colour-text-inverse));
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
}

.footer-bottom {
  width: 100%;
  padding: 16px 0;
  text-align: center;
  background: var(--app-footer-bottom-bg, transparent);
}

.footer-bottom-container {
  max-width: 1280px;
  margin: 0 auto;
  background: transparent;
}

.footer-bottom-link {
  color: var(--app-footer-bottom-text-color, var(--colour-base-text-colour));
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
}

.footer-bottom-link:hover {
  color: var(--colour-orange-text-colour);
}

.footer-bottom-version {
  color: var(--app-footer-bottom-text-color, var(--colour-base-text-colour));
  font-size: 14px;
  font-weight: 500;
}


@media (max-width: 1024px) {
  .info-grid-inner {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .app-footer {
    gap: 12px;
    padding-top: 16px;
  }

  .info-grid,
  .features-content {
    padding: 20px 12px;
  }

  .features-content-inner {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 12px;
  }

  .info-grid-inner {
    gap: 24px;
  }

  .info-section h2 {
    font-size: 20px;
  }

  .info-section p {
    font-size: 14px;
  }

  .feature-item {
    gap: 8px;
  }

  .feature-item h3 {
    white-space: normal;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.25;
  }

  .icon-wrapper {
    width: 36px;
    height: 36px;
  }

  .icon-wrapper img {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }
}
</style> 
